/**
 * Storage and Data Management Utilities
 * Theme management and data export/import functionality
 */

import { STORAGE_KEYS } from '@config/constants'

/**
 * Theme Management
 */

const THEME_STORAGE_KEY = STORAGE_KEYS.theme
const THEME_ATTRIBUTE = 'data-theme'

export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
} as const

export type Theme = (typeof THEMES)[keyof typeof THEMES]

/**
 * Gets the system's preferred color scheme
 */
export function getSystemTheme(): Theme {
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return THEMES.DARK
  }
  return THEMES.LIGHT
}

/**
 * Gets the stored theme preference (light/dark/system)
 */
export function getThemePreference(): Theme {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    if (stored === THEMES.LIGHT || stored === THEMES.DARK || stored === THEMES.SYSTEM) {
      return stored
    }
  } catch (error) {
    console.warn('Unable to read theme preference:', error)
  }
  return THEMES.SYSTEM
}

/**
 * Gets the current effective theme (resolves 'system' to 'light' or 'dark')
 */
export function getCurrentTheme(): Theme {
  const preference = getThemePreference()
  if (preference === THEMES.SYSTEM) {
    return getSystemTheme()
  }
  return preference
}

/**
 * Applies the current effective theme to the document
 */
export function applyTheme(): void {
  const effectiveTheme = getCurrentTheme()

  if (effectiveTheme === THEMES.DARK) {
    document.documentElement.setAttribute(THEME_ATTRIBUTE, THEMES.DARK)
  } else {
    document.documentElement.removeAttribute(THEME_ATTRIBUTE)
  }
}

/**
 * Sets the theme preference and applies it
 */
export function setTheme(theme: Theme): void {
  const validTheme =
    theme === THEMES.DARK ? THEMES.DARK : theme === THEMES.SYSTEM ? THEMES.SYSTEM : THEMES.LIGHT

  // Save preference
  try {
    localStorage.setItem(THEME_STORAGE_KEY, validTheme)
  } catch (error) {
    console.warn('Unable to save theme preference:', error)
  }

  // Apply the effective theme
  applyTheme()
}

/**
 * Toggles between light and dark theme
 */
export function toggleTheme(): Theme {
  const current = getCurrentTheme()
  const newTheme = current === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK
  setTheme(newTheme)
  return newTheme
}

/**
 * Initializes theme on page load and listens for system preference changes
 */
export function initializeTheme(): void {
  applyTheme()

  // Listen for system theme changes
  if (window.matchMedia) {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = () => {
      // Only re-apply if user has system preference selected
      if (getThemePreference() === THEMES.SYSTEM) {
        applyTheme()
      }
    }

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
    } else if ('addListener' in mediaQuery) {
      // Legacy browsers
      ;(mediaQuery as MediaQueryList).addListener(handleChange)
    }
  }
}

/**
 * Data Export/Import
 */

/**
 * Exports application data as a JSON file
 */
export function exportData(data: unknown, filename = 'flashcard-data.json'): void {
  try {
    const jsonString = JSON.stringify(data, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Export failed:', error)
    throw new Error('Failed to export data')
  }
}

/**
 * Imports application data from a JSON file
 */
export function importData(file: File): Promise<unknown> {
  return new Promise((resolve, reject) => {
    if (!file || file.type !== 'application/json') {
      reject(new Error('Please select a valid JSON file'))
      return
    }

    const reader = new FileReader()

    reader.onload = event => {
      try {
        const data = JSON.parse(event.target?.result as string)
        resolve(data)
      } catch (_error) {
        reject(new Error('Invalid JSON file format'))
      }
    }

    reader.onerror = () => {
      reject(new Error('Failed to read file'))
    }

    reader.readAsText(file)
  })
}
